import { RubleSVG } from '@sbercloud/uikit-product-icons';
import { HeroMain, HeroMainProps } from '@sbercloud/uikit-product-site-hero';
import { useRouter } from 'next/navigation';
import { AnchorMenuProps } from 'src/components/ProductDetailed/AnchorMenu';
import { useLayoutType } from 'src/hooks/useLayoutType';
import { ClickAnalyticsType } from 'src/types';
import { ProductTag } from 'src/types/backendContent';
import { type Breadcrumb, Breadcrumbs } from 'src/uikit/Breadcrumbs';
import { Target, Variant } from 'src/uikit/Button/constants';

import { useAnalytics } from '../../hooks/useAnalytics';
import { useHeaderPosition } from '../../hooks/useHeaderPosition';
import { scrollToElementWithId } from '../../utils/scrollToElementWithId';
import { SpeechBubble2 } from '../icons/ContentIcons/TechIcons';

export type ButtonProps = {
  title: string;
  variant?: Variant;
  className?: string;
  href?: string;
  isLink?: boolean;
  target?: Target;
  analyticsOptions: ClickAnalyticsType;
  onClick?: () => void;
  disabled?: boolean;
};

type BreadcrumbsProps = {
  links?: Breadcrumb[];
  color?: typeof Breadcrumbs.colors;
  className?: string;
  positionAbsolute?: boolean;
};

export type HeroInnerPageProps = {
  title: string;
  description?: string;
  tags?: ProductTag[];
  heroImage?: string;
  video?: HeroMainProps['video'];
  breadcrumbs?: BreadcrumbsProps;
  buttons?: ButtonProps[];
  className?: string;
  anchorMenu?: AnchorMenuProps;
  promo?: string;
  backgroundColor?: HeroMainProps['backgroundColor'];
};

export function HeroInnerPage({
  title,
  description,
  buttons: buttonsProp,
  tags,
  heroImage,
  video,
  breadcrumbs,
  className,
  anchorMenu,
  backgroundColor,
}: HeroInnerPageProps) {
  const layoutType = useLayoutType();
  const router = useRouter();

  const { showHeader, headerHeight } = useHeaderPosition();
  const { clickAnalytics } = useAnalytics();

  const getNavbarProps = (): HeroMainProps['navbar'] | undefined => {
    if (!anchorMenu) {
      return undefined;
    }

    const anchorItems: NonNullable<HeroMainProps['navbar']>['items'] =
      anchorMenu.anchors.map((anchor) => {
        let icon;

        if (anchor.type === 'price') {
          icon = <RubleSVG />;
        }

        if (anchor.type === 'question') {
          icon = <SpeechBubble2 />;
        }

        return {
          id: anchor.id,
          text: anchor.name,
          icon,
        };
      });

    return {
      items: anchorItems,
      onItemClick: (id, name) => {
        clickAnalytics({
          action: 'click',
          clickZone: 'body',
          clickElement: 'navbar',
          clickContent: name,
          uniqueId: `${anchorMenu?.slug}-navbar-${name}`,
          transitionType: 'inside-link',
        });

        scrollToElementWithId(id);
      },
      topPosition: showHeader ? headerHeight : undefined,
    };
  };

  const breadcrumbsItems: HeroMainProps['breadcrumbs'] =
    breadcrumbs?.links?.map((link) => ({
      id: link.title ?? link.link ?? '',
      href: link.link,
      label: link.title ?? '',
      onClick: (e) => {
        if (link.link && !e.metaKey) {
          e.preventDefault();
          router.push(link.link);
        }
      },
    })) ?? [];

  const buttons: HeroMainProps['buttons'] = buttonsProp?.map(
    ({ analyticsOptions, ...button }) => ({
      label: button.title,
      href: button.href ?? '',
      target: button.target,
      disabled: button.disabled,
      onClick(e) {
        if (button.href && button.target !== '_blank') {
          e.preventDefault();
          router.push(button.href);
        }

        clickAnalytics(analyticsOptions);
        button.onClick?.();
      },
    }),
  ) as HeroMainProps['buttons'];

  return (
    <HeroMain
      layoutType={layoutType}
      title={title}
      description={description ?? ''}
      image={heroImage}
      video={video}
      className={className}
      breadcrumbs={breadcrumbsItems}
      backgroundColor={backgroundColor ?? 'neutral-background'}
      navbar={getNavbarProps()}
      buttons={buttons}
      tags={tags?.map((tag) => ({
        text: tag.label || '',
        appearance: tag.appearance || 'neutral',
        tip: tag.tooltip || '',
      }))}
    />
  );
}
